import React, { useState } from 'react';
import { collection, getDocs, getDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/fire';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import { FaFileExcel, FaChartBar } from 'react-icons/fa';
import LoadingOverlay from './LoadingOverlay';

export default function ClassPerformanceReport({ theme = 'light' }) {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [taxRate, setTaxRate] = useState(18);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generated, setGenerated] = useState(false);

    const isDark = theme === 'dark';

    const normalizeEmail = (email) =>
        typeof email === 'string' ? email.trim().toLowerCase() : '';

    // Returns individual payments and their total for a member within the date range
    const getMemberStripePayments = (member, stripeCustomers, stripePaymentIntents) => {
        if (!stripeCustomers || !member) return { payments: [], total: 0 };

        let customer = null;
        if (member.stripeCustomerId) {
            customer = stripeCustomers.find(c => c.id === member.stripeCustomerId);
        }
        if (!customer) {
            customer = stripeCustomers.find(
                c => normalizeEmail(c.email) === normalizeEmail(member.email)
            );
        }
        if (!customer) return { payments: [], total: 0 };

        const startTs = startDate ? Math.floor(startDate.getTime() / 1000) : 0;
        // use a new Date to avoid mutating state
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        const endTs = endDate ? Math.floor(endOfDay.getTime() / 1000) : Infinity;

        const matched = (stripePaymentIntents || [])
            .filter(
                pi =>
                    pi.customer === customer.id &&
                    pi.status === 'succeeded' &&
                    pi.created >= startTs &&
                    pi.created <= endTs
            )
            .sort((a, b) => a.created - b.created);

        const payments = matched.map(pi => ({
            date: new Date(pi.created * 1000).toLocaleDateString('en-AU'),
            amount: pi.amount / 100,
        }));

        const total = payments.reduce((sum, p) => sum + p.amount, 0);
        return { payments, total };
    };

    const handleGenerate = async () => {
        if (!startDate || !endDate) {
            alert('Please select both a start and end date.');
            return;
        }
        if (startDate > endDate) {
            alert('Start date must be before end date.');
            return;
        }

        setLoading(true);
        try {
            const [categoriesSnap, coachesSnap, membersSnap, customersDoc, paymentIntentsDoc] =
                await Promise.all([
                    getDocs(collection(db, 'categories')),
                    getDocs(collection(db, 'coaches')),
                    getDocs(collection(db, 'members')),
                    getDoc(doc(db, 'stripe_exports', 'customers')),
                    getDoc(doc(db, 'stripe_exports', 'paymentIntents')),
                ]);

            const categories = categoriesSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => a.name.localeCompare(b.name));

            const coaches = coachesSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(c => !c.archived);

            const members = membersSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(m => !m.archived);

            const stripeCustomers = customersDoc.exists() ? customersDoc.data().data || [] : [];
            const stripePaymentIntents = paymentIntentsDoc.exists()
                ? paymentIntentsDoc.data().data || []
                : [];

            const dateKey = `${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}`;
            const adjustmentsSnap = await getDocs(collection(db, 'reportAdjustments'));
            const savedAdjustments = {};
            adjustmentsSnap.docs.forEach(d => {
                const data = d.data();
                if (data.dateKey === dateKey) {
                    savedAdjustments[data.memberId] = data.adjustedAmount;
                }
            });

            const report = categories
                .map(cat => {
                    const catCoaches = coaches.filter(
                        c => Array.isArray(c.categories) && c.categories.includes(cat.name)
                    );

                    const catMembers = members
                        .filter(m => m.category === cat.name)
                        .map(m => {
                            const { payments, total: stripeTotal } = getMemberStripePayments(
                                m,
                                stripeCustomers,
                                stripePaymentIntents
                            );
                            const adjustedAmount =
                                savedAdjustments[m.id] !== undefined
                                    ? savedAdjustments[m.id]
                                    : stripeTotal;
                            return {
                                id: m.id,
                                name: m.name,
                                payments,       // [{date, amount}]
                                stripeTotal,    // sum of stripe payments
                                adjustedAmount, // editable override
                            };
                        });

                    return {
                        categoryId: cat.id,
                        categoryName: cat.name,
                        coaches: catCoaches,
                        members: catMembers,
                    };
                })
                .filter(cat => cat.members.length > 0 || cat.coaches.length > 0);

            setReportData(report);
            setGenerated(true);
        } catch (error) {
            console.error('Error generating report:', error);
            alert('Error generating report: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAmountChange = (catIdx, memIdx, value) => {
        setReportData(prev => {
            const updated = [...prev];
            updated[catIdx] = {
                ...updated[catIdx],
                members: updated[catIdx].members.map((m, i) =>
                    i === memIdx ? { ...m, adjustedAmount: value } : m
                ),
            };
            return updated;
        });
    };

    const handleAmountBlur = async (member, value) => {
        const numVal = parseFloat(value);
        const finalVal = isNaN(numVal) ? 0 : numVal;
        const dateKey = `${startDate.toISOString().split('T')[0]}_${endDate.toISOString().split('T')[0]}`;
        const docId = `${member.id}_${dateKey}`;
        try {
            await setDoc(doc(db, 'reportAdjustments', docId), {
                memberId: member.id,
                memberName: member.name,
                dateKey,
                adjustedAmount: finalVal,
                updatedAt: new Date(),
            });
        } catch (e) {
            console.error('Error saving adjustment:', e);
        }
    };

    const applyTax = (amount) => {
        const rate = parseFloat(taxRate) || 0;
        return amount * (1 - rate / 100);
    };

    const categoryTotal = (members) =>
        members.reduce((sum, m) => sum + (parseFloat(m.adjustedAmount) || 0), 0);

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();
        const startStr = startDate?.toLocaleDateString('en-AU') || '';
        const endStr = endDate?.toLocaleDateString('en-AU') || '';

        // Summary sheet
        const summaryRows = [];
        summaryRows.push(['Class Performance Report Summary']);
        summaryRows.push(['Period', `${startStr} – ${endStr}`]);
        summaryRows.push(['Tax & Fee', `${taxRate}%`]);
        summaryRows.push([]);
        summaryRows.push(['Category', 'Members', 'Adjusted Total ($)', 'After Tax & Fee ($)']);
        let grandTotal = 0;
        let grandAfterTax = 0;
        reportData.forEach(cat => {
            const total = categoryTotal(cat.members);
            const afterTax = applyTax(total);
            grandTotal += total;
            grandAfterTax += afterTax;
            summaryRows.push([cat.categoryName, cat.members.length, total.toFixed(2), afterTax.toFixed(2)]);
        });
        summaryRows.push([]);
        summaryRows.push(['GRAND TOTAL', '', grandTotal.toFixed(2), grandAfterTax.toFixed(2)]);
        const summaryWs = XLSX.utils.aoa_to_sheet(summaryRows);
        summaryWs['!cols'] = [{ wch: 28 }, { wch: 12 }, { wch: 20 }, { wch: 22 }];
        XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

        reportData.forEach(cat => {
            const rows = [];
            rows.push(['Period', 'Start', startStr, 'End', endStr]);
            rows.push(['Tax and Transaction Fee', `${taxRate}%`]);
            rows.push([]);
            rows.push(['Coaches', cat.coaches.map(c => c.name).join(', ')]);
            rows.push([]);
            rows.push(['Student', 'Payment Date', 'Payment ($)', 'Adjusted Total ($)', 'After Tax & Fee ($)']);

            cat.members.forEach(m => {
                const adj = parseFloat(m.adjustedAmount) || 0;
                if (m.payments.length === 0) {
                    rows.push([m.name, 'No Stripe payments', '', adj.toFixed(2), applyTax(adj).toFixed(2)]);
                } else {
                    // First payment row includes member name
                    rows.push([m.name, m.payments[0].date, m.payments[0].amount.toFixed(2), '', '']);
                    // Remaining payment rows
                    m.payments.slice(1).forEach(p => {
                        rows.push(['', p.date, p.amount.toFixed(2), '', '']);
                    });
                    // Total row for member
                    rows.push(['', 'Total', m.stripeTotal.toFixed(2), adj.toFixed(2), applyTax(adj).toFixed(2)]);
                }
                rows.push([]);
            });

            const total = categoryTotal(cat.members);
            rows.push(['CATEGORY TOTAL', '', '', total.toFixed(2), applyTax(total).toFixed(2)]);

            const ws = XLSX.utils.aoa_to_sheet(rows);
            ws['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 20 }];
            XLSX.utils.book_append_sheet(wb, ws, cat.categoryName.substring(0, 31));
        });

        const fileName = `ClassPerformanceReport_${startDate?.toISOString().split('T')[0]}_${endDate?.toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const inputCls = `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isDark
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900'
    }`;

    const cellBase = `px-4 py-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`;

    return (
        <LoadingOverlay isLoading={loading}>
            <div className="p-4 sm:p-6 h-full overflow-auto">
                <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Class Performance Report
                </h2>
                <p className={`text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Shows each Stripe payment received per member within the selected period.
                    Adjust the total for concessions or cash members — saved automatically on blur.
                </p>

                {/* Filters */}
                <div className="flex flex-wrap gap-4 items-end mb-8">
                    <div>
                        <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            Start Date
                        </label>
                        <DatePicker
                            selected={startDate}
                            onChange={date => setStartDate(date)}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className={inputCls}
                            popperPlacement="bottom-start"
                            popperProps={{ strategy: 'fixed' }}
                        />
                    </div>
                    <div>
                        <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            End Date
                        </label>
                        <DatePicker
                            selected={endDate}
                            onChange={date => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className={inputCls}
                            popperPlacement="bottom-start"
                            popperProps={{ strategy: 'fixed' }}
                        />
                    </div>
                    <div>
                        <label className={`block text-xs font-semibold mb-1 uppercase tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            Tax & Fee %
                        </label>
                        <input
                            type="number"
                            value={taxRate}
                            onChange={e => setTaxRate(e.target.value)}
                            min="0"
                            max="100"
                            step="0.1"
                            className={`w-24 ${inputCls}`}
                        />
                    </div>
                    <button
                        onClick={handleGenerate}
                        className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 font-semibold text-sm"
                    >
                        <FaChartBar /> Generate
                    </button>
                    {generated && reportData.length > 0 && (
                        <button
                            onClick={handleExportExcel}
                            className="flex items-center gap-2 bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 font-semibold text-sm"
                        >
                            <FaFileExcel /> Export Excel
                        </button>
                    )}
                </div>

                {/* Empty state */}
                {generated && reportData.length === 0 && (
                    <div className={`rounded-xl p-8 text-center border ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'}`}>
                        <p className="text-sm">
                            No data found. Make sure members and coaches have categories assigned.
                        </p>
                    </div>
                )}

                {/* Report sections */}
                {reportData.map((cat, catIdx) => {
                    const catTotalAdj = categoryTotal(cat.members);
                    const catTotalAfterTax = applyTax(catTotalAdj);

                    return (
                        <div
                            key={cat.categoryId}
                            className={`mb-8 rounded-xl overflow-hidden border shadow-sm ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                        >
                            {/* Category header */}
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-3 flex items-center justify-between">
                                <h3 className="text-white font-bold text-lg tracking-wide">
                                    {cat.categoryName}
                                </h3>
                                <span className="text-blue-100 text-xs font-medium">
                                    {cat.members.length} member{cat.members.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            {/* Coach strip */}
                            <div className={`px-5 py-3 border-b flex flex-wrap items-center gap-3 ${
                                isDark ? 'bg-gray-700 border-gray-600' : 'bg-slate-50 border-gray-200'
                            }`}>
                                <span className={`text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${
                                    isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    {cat.coaches.length > 0
                                        ? `${cat.coaches.length} Coach${cat.coaches.length > 1 ? 'es' : ''} sharing income`
                                        : 'No coaches assigned'}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {cat.coaches.map(coach => (
                                        <span
                                            key={coach.id}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${
                                                isDark
                                                    ? 'bg-blue-900 border-blue-700 text-blue-200'
                                                    : 'bg-blue-50 border-blue-200 text-blue-700'
                                            }`}
                                        >
                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                                                isDark ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white'
                                            }`}>
                                                {coach.name.charAt(0).toUpperCase()}
                                            </span>
                                            {coach.name}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {cat.members.length === 0 ? (
                                <div className={`px-5 py-4 text-sm italic ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-white text-gray-400'}`}>
                                    No members assigned to this category.
                                </div>
                            ) : (
                                <table className={`w-full text-sm ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                                    <thead>
                                        <tr className={isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-600'}>
                                            <th className="px-4 py-2 text-left font-semibold w-40">Student</th>
                                            <th className="px-4 py-2 text-left font-semibold">Payment Date</th>
                                            <th className="px-4 py-2 text-right font-semibold">Payment ($)</th>
                                            <th className="px-4 py-2 text-right font-semibold">Adjusted Total ($)</th>
                                            <th className="px-4 py-2 text-right font-semibold">After Tax & Fee ($)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cat.members.map((member, memIdx) => {
                                            const adj = parseFloat(member.adjustedAmount) || 0;
                                            return (
                                                <React.Fragment key={member.id}>
                                                    {/* Member name sub-header */}
                                                    <tr className={`border-t-2 ${isDark ? 'border-gray-600 bg-gray-750' : 'border-gray-200 bg-blue-50'}`}>
                                                        <td
                                                            colSpan={5}
                                                            className={`px-4 py-2 font-semibold text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}
                                                        >
                                                            {member.name}
                                                        </td>
                                                    </tr>

                                                    {/* Individual payment rows */}
                                                    {member.payments.length === 0 ? (
                                                        <tr className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                                            <td className={`px-4 py-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}></td>
                                                            <td
                                                                colSpan={2}
                                                                className={`px-4 py-1.5 italic text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                                                            >
                                                                No Stripe payments in this period
                                                            </td>
                                                            <td></td>
                                                            <td></td>
                                                        </tr>
                                                    ) : (
                                                        member.payments.map((p, pIdx) => (
                                                            <tr
                                                                key={pIdx}
                                                                className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}
                                                            >
                                                                <td className={`px-4 py-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}></td>
                                                                <td className={`px-4 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                                    {p.date}
                                                                </td>
                                                                <td className={`px-4 py-1.5 text-right ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                                    {p.amount.toFixed(2)}
                                                                </td>
                                                                <td></td>
                                                                <td></td>
                                                            </tr>
                                                        ))
                                                    )}

                                                    {/* Adjusted total row for member */}
                                                    <tr className={`border-t ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                                                        <td className={`px-4 py-2 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></td>
                                                        <td className={`px-4 py-2 text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                            Total
                                                            {member.stripeTotal !== adj && (
                                                                <span className={`ml-2 font-normal italic ${isDark ? 'text-yellow-400' : 'text-orange-500'}`}>
                                                                    (adjusted from {member.stripeTotal.toFixed(2)})
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className={`px-4 py-2 text-right text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            {member.stripeTotal.toFixed(2)}
                                                        </td>
                                                        <td className="px-4 py-2 text-right">
                                                            <input
                                                                type="number"
                                                                value={member.adjustedAmount}
                                                                step="0.01"
                                                                min="0"
                                                                onChange={e => handleAmountChange(catIdx, memIdx, e.target.value)}
                                                                onBlur={e => handleAmountBlur(member, e.target.value)}
                                                                className={`w-28 px-2 py-1 border rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                                                                    isDark
                                                                        ? 'bg-gray-600 border-gray-500 text-white'
                                                                        : 'bg-white border-gray-300 text-gray-900'
                                                                }`}
                                                                title="Edit to apply concession or manual override. Saved automatically."
                                                            />
                                                        </td>
                                                        <td className={`px-4 py-2 text-right font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                                                            {applyTax(adj).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}

                                        {/* Category grand total */}
                                        <tr className={`border-t-2 font-bold ${isDark ? 'border-gray-500 bg-gray-700 text-white' : 'border-gray-400 bg-blue-600 text-white'}`}>
                                            <td className="px-4 py-3" colSpan={3}>Category Total</td>
                                            <td className="px-4 py-3 text-right">{catTotalAdj.toFixed(2)}</td>
                                            <td className="px-4 py-3 text-right">{catTotalAfterTax.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    );
                })}
            </div>
        </LoadingOverlay>
    );
}
